import type { TellescopeFieldResponse } from "../../types";

const FIELD_TOKEN_PREFIX = "{{field.";
const FIELD_TOKEN_SUFFIX = ".value}}";
const LOGIC_TOKEN = "{{logic}}";

/**
 * The answer text a routing tag can be built from.
 *
 * Deliberately narrower than a general answer stringifier: these values are matched against user
 * tags on the booking page, so only the shapes an author would plausibly route on resolve. Anything
 * structural (a signature, a file, an address) has no meaningful single-tag form and yields nothing,
 * which leaves the token unresolved rather than routing against a stringified object.
 */
const answerToTags = (answer: TellescopeFieldResponse["answer"] | undefined): string[] | undefined => {
  if (!answer?.value) return undefined;

  if (answer.type === "Insurance") return answer.value.payerName ? [answer.value.payerName] : undefined;

  const { value } = answer;
  if (typeof value === "string") return [value];
  if (typeof value === "number" || typeof value === "boolean") return [String(value)];
  if (Array.isArray(value) && value.every((entry) => typeof entry === "string")) return value;

  return undefined;
};

export interface ResolveBookingTagsArgs {
  /** A `userTags` / `userFilterTags` option array, entries possibly holding tokens. */
  tags: string[];
  responses: TellescopeFieldResponse[];
  /** The form's `logic` URL param, resolving a `{{logic}}` token. */
  logic?: string;
}

/**
 * Expands a booking field's configured tag list into the flat tags appended to the booking URL.
 *
 * Two token forms are recognized per entry: `{{logic}}` resolves to the form's `logic` param, and
 * `{{field.<id>.value}}` resolves to that field's answer — a multi-select expanding into one tag per
 * selection, so a single configured entry can contribute several tags.
 *
 * An unresolvable token is returned **verbatim**, matching legacy. That is deliberate: the booking
 * page treats an empty tag as "match anything", so dropping the token would silently widen the
 * provider pool instead of failing to narrow it.
 */
export const resolveBookingTags = ({ tags, responses, logic }: ResolveBookingTagsArgs): string[] =>
  tags.flatMap((tag) => {
    if (tag === LOGIC_TOKEN) return logic || tag;

    if (!tag.startsWith(FIELD_TOKEN_PREFIX) || !tag.endsWith(FIELD_TOKEN_SUFFIX)) return tag;

    const fieldId = tag.slice(FIELD_TOKEN_PREFIX.length, -FIELD_TOKEN_SUFFIX.length);
    const answer = responses.find((r) => r.fieldId === fieldId)?.answer;

    return answerToTags(answer) ?? tag;
  });
